import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useApproveCertificate } from '../../../hooks/api/certificates/useApproveCertificate';
import { useRejectCertificate } from '../../../hooks/api/certificates/useRejectCertificate';
import { Certificate } from '../../../hooks/api/utils/types';
import { setErrorsOnForm } from '../../../libs/utils';
import InputContainer from '../../forms/hookfields/InputContainer';
import { camelCaseToWords } from '@shared/utils/helpers';

const schema = yup.object().shape({
  expiresAt: yup.date().nullable().default(null).label('Expiration date'),
  publicComment: yup.string().nullable().label('Public remark'),
  internalComment: yup.string().nullable().label('Internal comment'),
});

type FormType = {
  publicComment: string;
  internalComment: string;
  expiresAt: null | string;
  action: string;
};

type Props = {
  certificate: Certificate;
  onSuccess: () => void;
  onCancel: () => void;
};

export default function ReviewCertificateForm({ certificate, onSuccess, onCancel }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
  } = useForm<FormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      expiresAt: certificate.expiresAt ? format(new Date(certificate.expiresAt), 'yyyy-MM-dd') : null,
      publicComment: certificate.publicComment || '',
      internalComment: certificate.internalComment || '',
    },
  });

  const { mutateAsync: reject } = useRejectCertificate();
  const { mutateAsync: approve } = useApproveCertificate();

  // @ts-ignore Idk, sometimes the date field is a string, sometimes it's a Date... I hate this.
  const onSubmit: SubmitHandler<FormType> = async (
    data: FormType & {
      expiresAt: Date;
    }
  ) => {
    console.log(data);
    try {
      if (data.action === 'approve') {
        await onSubmitApprove(data);
      } else {
        await onSubmitReject(data);
      }
    } catch (e) {
      setErrorsOnForm(setError, e);
      return;
    }
    reset();

    onSuccess();
  };

  const onSubmitReject = async (
    data: FormType & {
      expiresAt: Date;
    }
  ) => {
    return reject({
      id: certificate.id,
      publicComment: data.publicComment || null,
      internalComment: data.internalComment || null,
      expiresAt: data.expiresAt,
    });
  };

  const onSubmitApprove = async (
    data: FormType & {
      expiresAt: Date;
    }
  ) => {
    return approve({
      id: certificate.id,
      publicComment: data.publicComment || null,
      internalComment: data.internalComment || null,
      expiresAt: data.expiresAt!,
    });
  };
  return (
    <form className="m-login__form m-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="modal-body">
        <h6>{camelCaseToWords(certificate.type)}</h6>
        <p>{certificate.description}</p>

        <h6 className="mt-4">Expiration date</h6>
        {certificate.expiresAt ? <p>{format(new Date(certificate.expiresAt), 'dd/MM/yyyy')}</p> : <p>Not set</p>}

        <h6 className="mt-4">Uploaded files</h6>
        <div className="m-widget4">
          {certificate.files.map((file, index) => (
            <File key={index} file={file} />
          ))}
        </div>

        <InputContainer errors={errors} label={'Expiration date'} required={false} name={'expiresAt'}>
          <input type="date" className="form-control m-input" {...register('expiresAt')} />
        </InputContainer>

        <InputContainer
          errors={errors}
          label={'Remark for the company admin (optional)'}
          required={false}
          name={'publicComment'}
        >
          <textarea className="form-control m-input" {...register('publicComment')} />
        </InputContainer>

        <InputContainer errors={errors} label={'Internal comment (optional)'} required={false} name={'internalComment'}>
          <textarea className="form-control m-input" {...register('internalComment')} />
        </InputContainer>
      </div>
      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary"
          data-dismiss="modal"
          onClick={() => {
            reset();
            onCancel();
          }}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-success" name="approve" onClick={() => setValue('action', 'approve')}>
          Approve
        </button>
        <button type="submit" className="btn btn-danger" name="reject" onClick={() => setValue('action', 'reject')}>
          Reject
        </button>
      </div>
    </form>
  );
}

function FileTypeIcon({ file }: { file: any }) {
  // Available types: css, csv, doc, html, javascript, jpg, mp4, pdf, xml, zip,

  const extension = file.fileName.split('.').pop();
  let type = extension;

  if (extension === 'jpeg' || extension === 'png') {
    type = 'jpg';
  } else if (extension === 'js') {
    type = 'javascript';
  }

  return <img src={`../../assets/app/media/img/files/${type}.svg`} alt={file.name} />;
}

function File(props: { file: any }) {
  return (
    <div className="m-widget4__item">
      <div className="m-widget4__img m-widget4__img--icon">
        <FileTypeIcon file={props.file} />
      </div>
      <div className="m-widget4__info">
        <span className="m-widget4__text">{props.file.name}</span>
      </div>
      <div className="m-widget4__ext">
        <a href={props.file.downloadUrl} download className="m-widget4__icon">
          <i className="la la-download" />
        </a>
      </div>
    </div>
  );
}
